import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import { Box, Card, Link, Typography, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
// components
import { useAppContext } from '../../../context';

// ----------------------------------------------------------------------

const API_URL = process.env.REACT_APP_API_URL || 'https://api.ginoboxingstudio.com';

const FALLBACK_IMG = '/assets/images/products/event-default.jpg';

const StyledProductImg = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
});

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ----------------------------------------------------------------------

EventCard.propTypes = {
  event: PropTypes.object,
  booked: PropTypes.bool,
};

export default function EventCard({ event, booked = false }) {
  const { id, title, description, price, totalSeats, bookedSeats, date, imageUrl } = event;
  const { user } = useAppContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const seatsRemaining = Math.max(0, (totalSeats || 0) - (bookedSeats || 0));
  const isFull = seatsRemaining <= 0;
  const disabled = isFull || booked;
  let buttonLabel = 'Réserver';
  if (booked) buttonLabel = 'Déjà inscrit';
  else if (isFull) buttonLabel = 'Complet';

  const handleReserve = async () => {
    if (booked) return;
    if (!user?.user?.id) {
      navigate('/login', { replace: true });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${id}/checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.jwt}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result?.url) {
        // Redirection pleine page vers Stripe Checkout (évite le blocage de pop-up
        // qui surviendrait avec window.open après un appel asynchrone).
        window.location.href = result.url;
      } else {
        alert(result?.error?.message || 'Impossible de créer le paiement');
      }
    } catch (err) {
      alert('Une erreur est survenue, veuillez réessayer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Box sx={{ pt: '100%', position: 'relative' }}>
        <StyledProductImg alt={title} src={imageUrl || FALLBACK_IMG} />
      </Box>

      <Stack spacing={2} sx={{ p: 3 }}>
        <Link color="inherit" underline="hover">
          <Typography variant="subtitle2" noWrap>
            {title}
          </Typography>
        </Link>

        <Stack direction="column" alignItems="flex-start" justifyContent="space-between">
          {date && (
            <Typography variant="caption" color="text.secondary">
              {formatDate(date)}
            </Typography>
          )}
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
          <Typography variant="h4" sx={{ mt: 1 }}>
            {price}€
          </Typography>
          {booked ? (
            <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
              Vous êtes inscrit ✓
            </Typography>
          ) : (
            <Typography variant="subtitle2" color={isFull ? 'error' : 'text.secondary'}>
              {isFull ? 'Complet' : `${seatsRemaining} place${seatsRemaining > 1 ? 's' : ''} restante${seatsRemaining > 1 ? 's' : ''}`}
            </Typography>
          )}
        </Stack>

        <LoadingButton variant="contained" loading={loading} disabled={disabled} onClick={handleReserve}>
          {buttonLabel}
        </LoadingButton>
      </Stack>
    </Card>
  );
}
